import { SectionWrapper } from '../../../../components/layout/section-wrapper';
import { useGetEosAirQualityQuery } from '@client/api';
import { useDashboardFilters } from '../../../dashboard/context/dashboard-filters.context';
import { useMemo, useState, useRef, useEffect } from 'react';
import { ChartSectionState } from '../../../../components/charts/chart-section-state';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { ChartLegend } from '../../../../components/charts/types';
import { scaleLinear, scaleBand } from 'd3-scale';
import { area as d3area, line as d3line, curveMonotoneX } from 'd3-shape';
import { ChartTooltip } from '../../../../components/charts/tooltip';
import { getSeriesColor } from '../../../../utils/eos/getSeriesColor';
import { motion } from 'framer-motion';

const CO2_COLOR = getSeriesColor('co2');
const TEMP_COLOR = getSeriesColor('temperatureOut');
const THRESHOLD_COLOR = getSeriesColor('threshold');

export function AirQualityGraphSection() {
  const { t } = useTranslation('eos');
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();
  const hasAnimated = useRef(false);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    co2: number;
    temp: number;
    time: string;
  } | null>(null);

  const { data, isLoading, isError } = useGetEosAirQualityQuery(
    {
      from: selectedDateFromIso || '',
      to: selectedDateToIso || '',
      spaceIds: selectedSpaceIds,
    },
    {
      skip: !selectedDateFromIso || !selectedDateToIso || selectedSpaceIds.length === 0,
    },
  );

  // Reset animation when filters change
  useEffect(() => {
    hasAnimated.current = false;
  }, [selectedSpaceIds, selectedDateFromIso, selectedDateToIso]);

  const { co2Data, tempData, threshold } = useMemo(() => {
    if (!data) return { co2Data: [], tempData: [], threshold: 1000 };

    const co2Buckets = data.co2?.buckets || [];
    const tempBuckets = data.temperature?.buckets || [];

    return {
      co2Data: co2Buckets.map((b) => ({ key: b.key, value: b.value })),
      tempData: tempBuckets.map((b) => ({ key: b.key, value: b.value })),
      threshold: data.co2?.threshold || 1000,
    };
  }, [data]);

  const chartData = useMemo(() => {
    const allKeys = new Set([...co2Data.map((d) => d.key), ...tempData.map((d) => d.key)]);
    return Array.from(allKeys)
      .sort()
      .map((key) => ({
        key,
        co2: co2Data.find((d) => d.key === key)?.value || 0,
      }));
  }, [co2Data, tempData]);

  const legends = useMemo<ChartLegend[]>(
    () => [
      { id: 'co2', label: 'Luftkvalitet (CO₂)', color: CO2_COLOR },
      { id: 'temperature', label: 'Temperatur (°C)', color: TEMP_COLOR },
      { id: 'threshold', label: `Grenseverdi (${threshold} ppm)`, color: THRESHOLD_COLOR },
    ],
    [threshold],
  );

  if (isLoading) {
    return <ChartSectionState className="aspect-[10/4]">{t('loading')}</ChartSectionState>;
  }

  if (isError) {
    return <ChartSectionState className="aspect-[10/4]">{t('errorLoadingData')}</ChartSectionState>;
  }

  if (!selectedSpaceIds.length) {
    return <ChartSectionState className="aspect-[10/4]">{t('selectSpacesFirst')}</ChartSectionState>;
  }

  if (!data || chartData.length === 0) {
    return <ChartSectionState className="aspect-[10/4]">{t('noDataAvailable')}</ChartSectionState>;
  }

  return (
    <SectionWrapper>
      <div className="flex flex-col gap-4 w-full relative">
        {/* Tooltip */}
        {tooltip && (
          <div
            className="fixed z-50 pointer-events-none animate-in fade-in zoom-in-95 duration-150"
            style={{
              left: tooltip.x + 10,
              top: tooltip.y - 80,
            }}
          >
            <ChartTooltip>
              <div className="flex flex-col items-center min-w-[10em] gap-2">
                <div className="text-white font-semibold text-center">{tooltip.time}</div>
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Luftkvalitet (CO₂)</span>
                    </div>
                    <span className="text-sm font-semibold" style={{ color: CO2_COLOR }}>
                      {tooltip.co2.toFixed(0)} ppm
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Temperatur</span>
                    </div>
                    <span className="text-sm font-semibold" style={{ color: TEMP_COLOR }}>
                      {tooltip.temp.toFixed(1)} °C
                    </span>
                  </div>
                </div>
              </div>
            </ChartTooltip>
          </div>
        )}

        <div className="flex w-full aspect-[10/4]">
          <ResponsiveBar
            data={chartData}
            keys={[]}
            indexBy="key"
            margin={{ top: 20, right: 80, bottom: 50, left: 80 }}
            padding={0.3}
            colors={[]}
            enableLabel={false}
            enableGridY={true}
            enableGridX={true}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 10,
              tickRotation: 0,
              format: (value) => {
                const date = new Date(value);
                return date.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
              },
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: 5,
            }}
            theme={{
              axis: {
                ticks: {
                  text: { fill: 'rgba(255, 255, 255, 0.4)', fontSize: 11 },
                },
              },
              grid: {
                line: { stroke: 'rgba(255, 255, 255, 0.1)' },
              },
            }}
            layers={[
              'grid',
              'axes',
              (props: any) => {
                const { xScale, yScale, innerHeight, innerWidth } = props;

                // CO2 scale - must always show threshold + 100 ppm minimum
                const co2Values = co2Data.map((d) => d.value);
                if (co2Values.length === 0) return null;

                const co2Min = Math.min(...co2Values);
                const co2Max = Math.max(...co2Values);

                // Ensure Y-axis goes at least 100 ppm above threshold
                const minMaxValue = threshold + 100;
                const actualMax = Math.max(co2Max, minMaxValue);

                // Start from 0 to make CO2 area fill more of the chart
                const co2Scale = scaleLinear()
                  .domain([0, actualMax * 1.1])
                  .range([innerHeight, 0]);

                // Temperature scale for right Y-axis
                const tempValues = tempData.map((d) => d.value);
                if (tempValues.length === 0) return null;

                const tempMax = Math.max(...tempValues);
                const tempMin = Math.min(...tempValues);

                // Start from 0 to make temperature area fill more of the chart
                const tempScale = scaleLinear()
                  .domain([tempMin * 0.9, tempMax * 1.5])
                  .range([innerHeight, 0]);

                // Create CO2 area path
                const co2Area = d3area<{ key: string; value: number }>()
                  .x((d) => (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0))
                  .y0(innerHeight)
                  .y1((d) => co2Scale(d.value))
                  .curve(curveMonotoneX);

                const co2Path = co2Area(co2Data) || '';

                // Create CO2 line path (for stroke on top)
                const co2Line = d3line<{ key: string; value: number }>()
                  .x((d) => (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0))
                  .y((d) => co2Scale(d.value))
                  .curve(curveMonotoneX);

                const co2LinePath = co2Line(co2Data) || '';

                // Create temperature area path
                const tempArea = d3area<{ key: string; value: number }>()
                  .x((d) => (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0))
                  .y0(innerHeight)
                  .y1((d) => tempScale(d.value))
                  .curve(curveMonotoneX);

                const tempPath = tempArea(tempData) || '';

                // Create temperature line path (for stroke on top)
                const tempLine = d3line<{ key: string; value: number }>()
                  .x((d) => (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0))
                  .y((d) => tempScale(d.value))
                  .curve(curveMonotoneX);

                const tempLinePath = tempLine(tempData) || '';

                // Threshold line aligned to CO2 scale
                const thresholdY = co2Scale(threshold);

                // Left Y-axis ticks for CO2
                const co2Ticks = co2Scale.ticks(5);

                // Right Y-axis ticks for temperature
                const tempTicks = tempScale.ticks(5);

                return (
                  <g>
                    {/* Define gradients and clipPaths */}
                    <defs>
                      <linearGradient id="co2Gradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={CO2_COLOR} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={CO2_COLOR} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="tempGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={TEMP_COLOR} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={TEMP_COLOR} stopOpacity={0} />
                      </linearGradient>
                      <clipPath id="areaClipPath">
                        <motion.rect
                          x={0}
                          y={0}
                          width={innerWidth}
                          height={innerHeight}
                          initial={!hasAnimated.current ? { x: 0, width: 0 } : false}
                          animate={{ x: 0, width: innerWidth }}
                          transition={{ delay: 0.5, duration: 0.6, ease: 'easeInOut' }}
                        />
                      </clipPath>
                    </defs>

                    {/* Grid lines based on CO2 scale */}
                    {co2Ticks.map((tick, i) => {
                      const y = co2Scale(tick);
                      return (
                        <line
                          key={`grid-${i}`}
                          x1={0}
                          y1={y}
                          x2={innerWidth}
                          y2={y}
                          stroke="rgba(255, 255, 255, 0.1)"
                          strokeWidth={1}
                        />
                      );
                    })}

                    {/* Temperature area - drawn first (behind) */}
                    <path d={tempPath} fill="url(#tempGradient)" stroke="none" clipPath="url(#areaClipPath)" />

                    {/* Temperature line on top */}
                    <motion.path
                      d={tempLinePath}
                      fill="none"
                      stroke={TEMP_COLOR}
                      strokeWidth={2}
                      pathLength={1}
                      initial={!hasAnimated.current ? { pathLength: 0, opacity: 0 } : false}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.6, ease: 'easeInOut' }}
                    />

                    {/* CO2 area - drawn second (in front) */}
                    <path d={co2Path} fill="url(#co2Gradient)" stroke="none" clipPath="url(#areaClipPath)" />

                    {/* CO2 line on top */}
                    <motion.path
                      d={co2LinePath}
                      fill="none"
                      stroke={CO2_COLOR}
                      strokeWidth={2}
                      pathLength={1}
                      initial={!hasAnimated.current ? { pathLength: 0, opacity: 0 } : false}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.6, ease: 'easeInOut' }}
                      onAnimationComplete={() => {
                        hasAnimated.current = true;
                      }}
                    />

                    {/* Threshold line */}
                    <line
                      x1={0}
                      y1={thresholdY}
                      x2={innerWidth}
                      y2={thresholdY}
                      stroke={THRESHOLD_COLOR}
                      strokeWidth={2}
                      strokeDasharray="5,5"
                    />

                    {/* Temperature data points - drawn on top */}
                    {tempData.map((d, i) => {
                      const x = (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0);
                      const y = tempScale(d.value);
                      const co2Value = co2Data.find((cd) => cd.key === d.key)?.value || 0;
                      const date = new Date(d.key);
                      const timeString = date.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });

                      return (
                        <motion.circle
                          key={`temp-point-${i}`}
                          cx={x}
                          cy={y}
                          r={5}
                          fill={TEMP_COLOR}
                          stroke="white"
                          strokeWidth={0}
                          style={{ cursor: 'pointer' }}
                          initial={!hasAnimated.current ? { opacity: 0, scale: 0.6 } : false}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + Math.min(0.6 * 0.6, 0.45) + i * 0.01, duration: 0.2 }}
                          whileHover={{ scale: 1.4 }}
                          onMouseEnter={(e) => {
                            setTooltip({
                              x: e.clientX,
                              y: e.clientY,
                              co2: co2Value,
                              temp: d.value,
                              time: timeString,
                            });
                          }}
                          onMouseLeave={() => setTooltip(null)}
                        />
                      );
                    })}

                    {/* CO2 data points - drawn on top */}
                    {co2Data.map((d, i) => {
                      const x = (xScale(d.key) || 0) + (xScale.bandwidth ? xScale.bandwidth() / 2 : 0);
                      const y = co2Scale(d.value);
                      const tempValue = tempData.find((td) => td.key === d.key)?.value || 0;
                      const date = new Date(d.key);
                      const timeString = date.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });

                      return (
                        <motion.circle
                          key={`co2-point-${i}`}
                          cx={x}
                          cy={y}
                          r={5}
                          fill={CO2_COLOR}
                          stroke="white"
                          strokeWidth={0}
                          style={{ cursor: 'pointer' }}
                          initial={!hasAnimated.current ? { opacity: 0, scale: 0.6 } : false}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + Math.min(0.6 * 0.6, 0.45) + i * 0.01, duration: 0.2 }}
                          whileHover={{ scale: 1.4 }}
                          onMouseEnter={(e) => {
                            setTooltip({
                              x: e.clientX,
                              y: e.clientY,
                              co2: d.value,
                              temp: tempValue,
                              time: timeString,
                            });
                          }}
                          onMouseLeave={() => setTooltip(null)}
                        />
                      );
                    })}

                    {/* Left Y-axis for CO2 */}
                    <g>
                      {co2Ticks.map((tick, i) => {
                        const y = co2Scale(tick);
                        return (
                          <g key={`co2-tick-${i}`}>
                            <text
                              x={-10}
                              y={y}
                              dy="0.32em"
                              fontSize={11}
                              fill="rgba(255, 255, 255, 0.4)"
                              textAnchor="end"
                            >
                              {tick.toFixed(0)}
                            </text>
                          </g>
                        );
                      })}
                      <text
                        x={-60}
                        y={innerHeight / 2}
                        fontSize={12}
                        fill="rgba(255, 255, 255, 0.6)"
                        textAnchor="middle"
                        transform={`rotate(-90, -60, ${innerHeight / 2})`}
                      >
                        CO₂ (ppm)
                      </text>
                    </g>

                    {/* Right Y-axis for temperature */}
                    <g transform={`translate(${innerWidth}, 0)`}>
                      {tempTicks.map((tick, i) => {
                        const y = tempScale(tick);
                        return (
                          <g key={`temp-tick-${i}`}>
                            <text
                              x={10}
                              y={y}
                              dy="0.32em"
                              fontSize={11}
                              fill="rgba(255, 255, 255, 0.4)"
                              textAnchor="start"
                            >
                              {tick.toFixed(1)}
                            </text>
                          </g>
                        );
                      })}
                      <text
                        x={60}
                        y={innerHeight / 2}
                        fontSize={12}
                        fill="rgba(255, 255, 255, 0.6)"
                        textAnchor="middle"
                        transform={`rotate(-90, 60, ${innerHeight / 2})`}
                      >
                        Temperatur (°C)
                      </text>
                    </g>
                  </g>
                );
              },
            ]}
          />
        </div>
        <div className="flex gap-4 items-center justify-center mb-5">
          {legends.map((legend) => (
            <div key={legend.id} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: legend.color }} />
              <span className="text-xs text-white">{legend.label}</span>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
}
